import React from "react";
import {
  Box, Container, Stack, Typography, TextField, InputAdornment, Button, Chip, Grid, IconButton,
  AppBar, Toolbar, Drawer, Divider
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import SmartDisplayRoundedIcon from "@mui/icons-material/SmartDisplayRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import LocalFloristRoundedIcon from "@mui/icons-material/LocalFloristRounded";
import ForestRoundedIcon from "@mui/icons-material/ForestRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { motion } from "framer-motion";

const railWidth = 88;

const railMenus = [
  { key: "home", label: "ホーム", icon: <HomeRoundedIcon />, href: "/" },
  { key: "audio", label: "音源", icon: <LibraryMusicRoundedIcon />, href: "/audio" },
  { key: "video", label: "動画", icon: <SmartDisplayRoundedIcon />, href: "/video" },
  { key: "news", label: "お知らせ", icon: <CampaignRoundedIcon />, href: "/news" },
  { key: "community", label: "交流", icon: <ForumRoundedIcon />, href: "/community" },
];

const categories = [
  { label: "八重の誓い", icon: <LocalFloristRoundedIcon />, href: "/yae" },
  { label: "光の儀式", icon: <AutoAwesomeRoundedIcon />, href: "/rites" },
  { label: "誓いの森", icon: <ForestRoundedIcon />, href: "/forest" },
  { label: "神社クエスト", icon: <MapRoundedIcon />, href: "/shrine" },
  { label: "米部", icon: <FavoriteRoundedIcon />, href: "/rice" },
];

export default function MastermindHero() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [active, setActive] = React.useState("home"); // 選択状態

  const handleDrawerToggle = () => setMobileOpen((v) => !v);

  const rail = (
    <NavRail
      active={active}
      onSelect={(k, href) => {
        setActive(k);
        // TODO: ルーティングへ置き換え
        console.log("go:", href);
      }}
    />
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100dvh", bgcolor: "rgba(120,85,255,0.04)" }}>
      {/* モバイルのAppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { xs: "block", md: "none" },
          bgcolor: "transparent",
          color: "text.primary",
          borderBottom: "1px solid",
          borderColor: "divider",
          backdropFilter: "saturate(180%) blur(8px)",
        }}
      >
        <Toolbar>
          <IconButton edge="start" onClick={handleDrawerToggle}>
            <MenuRoundedIcon />
          </IconButton>
          <Typography sx={{ ml: 1, fontWeight: 800 }}>Mastermind</Typography>
        </Toolbar>
      </AppBar>

      {/* ナビゲーションレール */}
      <Box component="nav" aria-label="sidebar navigation">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { width: railWidth, boxSizing: "border-box", border: "none" },
          }}
        >
          {rail}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: railWidth,
              boxSizing: "border-box",
              border: "none",
              background:
                "linear-gradient(180deg, #F1ECFF 0%, #F6F7FF 60%, #FFFFFF 100%)",
              "&.mui-dark, [data-mui-color-scheme='dark'] &": {
                background: "linear-gradient(180deg, #1F1A2C 0%, #222637 60%, #1A1C26 100%)",
              },
            },
          }}
        >
          {rail}
        </Drawer>
      </Box>

      {/* メイン（角丸のカード感） */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${railWidth}px)` },
          ml: { md: `${railWidth}px` },
          p: { xs: 0, md: 2 },
        }}
      >
        {/* 角丸のラッパ */}
        <Box
          sx={{
            borderRadius: { xs: 0, md: 6 },
            overflow: "hidden",
            boxShadow: { md: "0 20px 60px rgba(20,16,40,0.08)" },
            bgcolor: "background.paper",
          }}
        >
          {/* モバイルのAppBar分の余白 */}
          <Toolbar sx={{ display: { xs: "block", md: "none" } }} />
          <HeroContent />
        </Box>
      </Box>
    </Box>
  );
}

/** Canva風 ナビゲーションレール（アイコンの下にテキスト／選択で色変化） */
function NavRail({ active, onSelect }) {
  return (
    <Box
      sx={{
        height: "100%",
        p: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
      }}
    >
      {/* ハンバーガーの余白用 */}
      <Box sx={{ height: 8 }} />
      <RailItem
        label="作成"
        icon="+"
        active={false}
        onClick={() => onSelect?.("create", "/create")}
        variant="primary"
      />
      <Divider sx={{ my: 1.5, width: 44, borderColor: "rgba(120,85,255,0.15)" }} />
      {railMenus.map((m) => (
        <RailItem
          key={m.key}
          label={m.label}
          icon={m.icon}
          active={active === m.key}
          onClick={() => onSelect?.(m.key, m.href)}
        />
      ))}
      <Box sx={{ flexGrow: 1 }} />
      <Typography variant="caption" sx={{ pb: 2, opacity: 0.55 }}>
        もっと見る
      </Typography>
    </Box>
  );
}

function RailItem({ label, icon, active, onClick, variant }) {
  return (
    <Stack
      alignItems="center"
      spacing={0.5}
      sx={{
        width: "100%",
        py: 0.75,
        cursor: "pointer",
        userSelect: "none",
        color: active ? "#5A35FF" : "rgba(60,40,120,0.8)",
        transition: "all .18s ease",
        "&:hover": { transform: "translateY(-1px)", color: "#5A35FF" },
      }}
      onClick={onClick}
    >
      <IconButton
        disableRipple
        sx={{
          width: 48, height: 48,
          borderRadius: 3,
          background:
            active
              ? "linear-gradient(180deg, rgba(125,92,255,0.18) 0%, rgba(0,183,255,0.14) 100%)"
              : "rgba(255,255,255,0.8)",
          boxShadow: active
            ? "inset 0 0 0 2px rgba(116,86,255,0.45), 0 10px 24px rgba(80,60,160,0.12)"
            : "0 8px 20px rgba(30,12,90,0.06)",
          "&:hover": {
            background: "linear-gradient(180deg, rgba(125,92,255,0.24) 0%, rgba(0,183,255,0.18) 100%)",
          },
        }}
      >
        {variant === "primary" ? (
          <Box
            component="span"
            sx={{
              fontWeight: 800,
              fontSize: 24,
              lineHeight: 1,
              color: "#5A35FF",
            }}
          >
            {icon}
          </Box>
        ) : (
          React.cloneElement(icon, { fontSize: "medium" })
        )}
      </IconButton>
      <Typography variant="caption" sx={{ fontWeight: active ? 800 : 600 }}>
        {label}
      </Typography>
    </Stack>
  );
}

function HeroContent() {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        pt: { xs: 10, sm: 12, md: 16 },
        pb: { xs: 8, sm: 10, md: 14 },
        background: "linear-gradient(180deg, #F5F2FF 0%, #F7FAFF 45%, #FFFFFF 100%)",
        "&.mui-dark, [data-mui-color-scheme='dark'] &": {
          background: "linear-gradient(180deg, #191925 0%, #1E2230 45%, #1A1C26 100%)",
        },
      }}
    >
      {/* ぼかしの光 */}
      <Box
        aria-hidden
        sx={{
          pointerEvents: "none",
          position: "absolute",
          inset: 0,
          "&::before, &::after": {
            content: '""',
            position: "absolute",
            borderRadius: "50%",
            filter: "blur(60px)",
            opacity: 0.6,
          },
          "&::before": {
            width: 360,
            height: 360,
            top: -80,
            left: -80,
            background: "radial-gradient(closest-side, rgba(172,143,255,0.35), rgba(172,143,255,0))",
          },
          "&::after": {
            width: 420,
            height: 420,
            right: -120,
            bottom: -100,
            background: "radial-gradient(closest-side, rgba(129,226,255,0.35), rgba(129,226,255,0))",
          },
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative" }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <IconButton
            size="small"
            disableRipple
            aria-label="Mastermind"
            sx={{ bgcolor: "white", boxShadow: "0 6px 24px rgba(0,0,0,0.08)", "&:hover": { bgcolor: "white" } }}
          >
            <AutoAwesomeRoundedIcon fontSize="small" />
          </IconButton>
          <Typography variant="overline" sx={{ letterSpacing: 1.4, opacity: 0.8 }}>
            MASTERMIND 2025
          </Typography>
        </Stack>

        <Stack spacing={3} sx={{ textAlign: "center", maxWidth: 920, mx: "auto" }}>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: 28, sm: 40, md: 52 },
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
                background: "linear-gradient(90deg, #6D4DFF 0%, #8B7BFF 35%, #00B7FF 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              さあ、何を探求しましょう？
            </Typography>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08 }}>
            <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: 400, lineHeight: 1.6 }}>
              知と光が交わる学びの拠点。<wbr />
              プロジェクトに触れ、仲間とつながり、次の一歩を見つけよう。
            </Typography>
          </motion.div>
        </Stack>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: { xs: 3, sm: 4 }, maxWidth: 800, mx: "auto" }}>
            <TextField
              fullWidth
              placeholder="プロジェクト、キーワード、ドキュメントを検索"
              aria-label="サイト内検索"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon />
                  </InputAdornment>
                ),
                sx: { pr: 0, bgcolor: "white", borderRadius: 3 },
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const q = e.target.value?.trim();
                  if (!q) return;
                  console.log("search:", q); // TODO: navigate(`/search?q=${encodeURIComponent(q)}`)
                }
              }}
            />
            <Button
              size="large"
              variant="contained"
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={() => console.log("primary CTA")}
              sx={{ borderRadius: 2.5, px: 3, fontWeight: 700, boxShadow: "0 12px 32px rgba(109,77,255,0.25)" }}
            >
              今すぐ始める
            </Button>
          </Stack>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.25 }}>
          <Grid container spacing={1} sx={{ mt: { xs: 3, sm: 4 }, maxWidth: 900, mx: "auto", justifyContent: "center" }}>
            {categories.map((c) => (
              <Grid item key={c.label}>
                <Chip
                  icon={c.icon}
                  label={c.label}
                  onClick={() => console.log("go:", c.href)}
                  sx={{
                    px: 1.2,
                    py: 1.2,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.8)",
                    backdropFilter: "saturate(180%) blur(8px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.95)", transform: "translateY(-1px)" },
                    transition: "all .18s ease",
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center" alignItems="center" sx={{ mt: { xs: 4, sm: 5 } }}>
            <Button variant="text" startIcon={<PlayArrowRoundedIcon />} onClick={() => console.log("watch intro")} sx={{ fontWeight: 700 }}>
              1分でわかるマスターマインド
            </Button>
            <Typography variant="body2" color="text.secondary">
              新しい季節の特集公開中
            </Typography>
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
}
